"use client";

import {
  type Cell,
  type Column,
  flexRender,
  type ReactTable,
  type RowData,
} from "@tanstack/react-table";
import { defaultRangeExtractor, useVirtualizer } from "@tanstack/react-virtual";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { TableColumnResizeHandle } from "@/components/ui/table-column-resize-handle";
import { InfiniteFeedLoader } from "@/components/app/inbox/infinite-feed-loader";
import { ACTIONS_COLUMN_ID, ROW_HEIGHT } from "./record-table-state";
import {
  RecordTableCellView,
  RecordTableGridView,
  RecordTableHeaderCellView,
  RecordTableHeaderRowView,
  RecordTableRowView,
} from "./record-table-grid-view";
import {
  nextRecordTableCell,
  type RecordTableArrowKey,
  type RecordTableCell,
} from "./record-table-navigation";
import { createRecordTableFocusScheduler } from "./record-table-focus";
import type { GridTableFeatures } from "../../table-features";

const OVERSCAN_ROWS = 12;

function RecordTableCellContent<TData extends RowData>({
  cell,
  options,
  state,
}: {
  cell: Cell<GridTableFeatures, TData, unknown>;
  options: ReactTable<GridTableFeatures, TData>["options"];
  state: ReactTable<GridTableFeatures, TData>["state"];
}) {
  // Virtualizer updates only change row geometry. Keep rich cells mounted without
  // rendering them again. v9 caches cell contexts around a mutable core table, so
  // capture the options/state snapshots too: edits, metadata callbacks and table
  // state must invalidate content even when the cell/context identity is stable.
  return useMemo(
    () => flexRender(cell.column.columnDef.cell, cell.getContext()),
    [cell, options, state],
  );
}

export function RecordTableGrid<TData extends RowData>({
  table,
  scrollRef,
  caption,
  activeCell,
  editingCell,
  canEditCell,
  editOnClick,
  onSelectCell,
  onEditCell,
  empty,
  renderFooter,
  hasMore = false,
  isFetchingMore = false,
  paginationError,
  isRefreshing = false,
  onLoadMore,
  emptyMessage = "No records yet.",
  sort = null,
  canSortColumn,
  onSortColumn,
}: {
  table: ReactTable<GridTableFeatures, TData>;
  scrollRef: React.RefObject<HTMLDivElement | null>;
  caption?: string;
  activeCell: RecordTableCell | null;
  editingCell: RecordTableCell | null;
  canEditCell: (cell: RecordTableCell) => boolean;
  editOnClick: (cell: RecordTableCell) => boolean;
  onSelectCell: (cell: RecordTableCell, record: TData) => void;
  onEditCell: (cell: RecordTableCell) => void;
  empty: boolean;
  /** The sorted column, mirrored in its header. */
  sort?: { columnId: string; direction: "asc" | "desc" } | null;
  canSortColumn?: (columnId: string) => boolean;
  /** A header click; the owner cycles ascending, descending, off. */
  onSortColumn?: (columnId: string) => void;
  renderFooter?: (props: {
    columns: Column<GridTableFeatures, TData, unknown>[];
    tableWidth: number;
  }) => React.ReactNode;
  hasMore?: boolean;
  isFetchingMore?: boolean;
  paginationError?: Error | null;
  isRefreshing?: boolean;
  onLoadMore?: () => void;
  emptyMessage?: string;
}) {
  const rowModel = table.getRowModel();
  const editingIndex = editingCell
    ? rowModel.rows.findIndex((row) => row.id === editingCell.rowId)
    : -1;
  const virtualizer = useVirtualizer({
    count: rowModel.rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: OVERSCAN_ROWS,
    getItemKey: (index) => rowModel.rows[index]!.id,
    // Keep an unsaved text editor mounted when its row leaves the viewport.
    rangeExtractor: (range) => {
      const indices = defaultRangeExtractor(range);
      return editingIndex < 0 || indices.includes(editingIndex)
        ? indices
        : [...indices, editingIndex].sort((a, b) => a - b);
    },
  });
  const virtualRows = virtualizer.getVirtualItems();
  const tableWidth = table.getTotalSize();
  const lastVirtualIndex = virtualizer.range?.endIndex ?? -1;
  const cellRefs = useRef(new Map<string, HTMLTableCellElement>());
  const lastScrolledCell = useRef<RecordTableCell | null>(null);
  const pendingFocus = useMemo(
    () =>
      createRecordTableFocusScheduler(
        (callback) => window.requestAnimationFrame(callback),
        (id) => window.cancelAnimationFrame(id),
      ),
    [],
  );
  useEffect(() => () => pendingFocus.cancel(), [pendingFocus]);
  const dataColumns = table
    .getAllLeafColumns()
    .filter((column) => column.id !== ACTIONS_COLUMN_ID);
  const activeIndex = activeCell
    ? rowModel.rows.findIndex((row) => row.id === activeCell.rowId)
    : -1;
  const activeVisible =
    activeIndex >= (virtualizer.range?.startIndex ?? 0) &&
    activeIndex <= (virtualizer.range?.endIndex ?? -1) &&
    dataColumns.some((column) => column.id === activeCell?.columnId);
  const entryRow =
    rowModel.rows[virtualizer.range?.startIndex ?? virtualRows[0]?.index ?? 0];
  const entryCell = activeVisible
    ? activeCell
    : entryRow && dataColumns[0]
      ? { rowId: entryRow.id, columnId: dataColumns[0].id }
      : null;
  const focusCell = useCallback(
    (cell: RecordTableCell, edit = false) => {
      pendingFocus.cancel();
      const rowIndex = rowModel.rows.findIndex((row) => row.id === cell.rowId);
      if (rowIndex < 0) return;
      virtualizer.scrollToIndex(rowIndex, { align: "auto" });
      const focus = () => {
        const element = cellRefs.current.get(`${cell.rowId}:${cell.columnId}`);
        if (edit) {
          // An auto-opened dropdown owns focus inside its portal.
          if (element?.querySelector('[aria-haspopup][aria-expanded="true"]'))
            return;
          element
            ?.querySelector<HTMLElement>(
              "input, textarea, select, button, [role='combobox']",
            )
            ?.focus();
        } else {
          element?.focus();
        }
      };
      if (!edit && cellRefs.current.has(`${cell.rowId}:${cell.columnId}`))
        focus();
      else pendingFocus.schedule(focus);
    },
    [pendingFocus, rowModel.rows, virtualizer],
  );

  useEffect(() => {
    if (
      !hasMore ||
      isFetchingMore ||
      paginationError ||
      isRefreshing ||
      !onLoadMore ||
      rowModel.rows.length === 0
    )
      return;
    if (lastVirtualIndex >= rowModel.rows.length - 12) onLoadMore();
  }, [
    hasMore,
    isFetchingMore,
    paginationError,
    isRefreshing,
    lastVirtualIndex,
    onLoadMore,
    rowModel.rows.length,
  ]);

  useEffect(() => {
    if (!activeCell) return;
    if (
      lastScrolledCell.current?.rowId === activeCell.rowId &&
      lastScrolledCell.current?.columnId === activeCell.columnId
    ) return;
    const rowIndex = rowModel.rows.findIndex(
      (row) => row.id === activeCell.rowId,
    );
    if (rowIndex >= 0) {
      lastScrolledCell.current = activeCell;
      virtualizer.scrollToIndex(rowIndex, { align: "auto" });
    }
  }, [activeCell, rowModel.rows, virtualizer]);

  return (
    <RecordTableGridView
      scrollRef={scrollRef}
      caption={caption ?? "Records"}
      rowCount={
        hasMore
          ? -1
          : rowModel.rows.length +
            table.getHeaderGroups().length +
            (renderFooter ? 1 : 0)
      }
      columnCount={table.getAllLeafColumns().length}
      busy={isRefreshing}
      tableWidth={tableWidth}
      bodyHeight={virtualizer.getTotalSize()}
      header={table.getHeaderGroups().map((headerGroup) => (
        <RecordTableHeaderRowView key={headerGroup.id}>
          {headerGroup.headers.map((header) => {
            const name = String(header.column.columnDef.header ?? "column");
            return (
              <RecordTableHeaderCellView
                key={header.id}
                width={header.getSize()}
                pinned={header.column.id === ACTIONS_COLUMN_ID}
                label={
                  header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )
                }
                name={name}
                sortable={
                  Boolean(onSortColumn) &&
                  (canSortColumn?.(header.column.id) ?? false)
                }
                sorted={
                  sort?.columnId === header.column.id ? sort.direction : null
                }
                onSort={() => onSortColumn?.(header.column.id)}
                resizeHandle={
                  header.column.getCanResize() ? (
                    <TableColumnResizeHandle
                      aria-label={`Resize ${name}`}
                      active={header.column.getIsResizing()}
                      width={header.getSize()}
                      minWidth={header.column.columnDef.minSize ?? 20}
                      maxWidth={header.column.columnDef.maxSize ?? 1000}
                      onResize={(width) =>
                        table.setColumnSizing((current) => ({
                          ...current,
                          [header.column.id]: width,
                        }))
                      }
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                    />
                  ) : null
                }
              />
            );
          })}
        </RecordTableHeaderRowView>
      ))}
      footer={renderFooter?.({
        columns: table.getAllLeafColumns(),
        tableWidth,
      })}
      emptyMessage={empty ? emptyMessage : null}
      loader={
        !empty && onLoadMore ? (
          <InfiniteFeedLoader
            hasMore={hasMore}
            isLoading={isFetchingMore || isRefreshing}
            error={paginationError}
            noun="records"
            onLoadMore={() => {
              if (!isRefreshing) onLoadMore();
            }}
          />
        ) : null
      }
    >
      {virtualRows.map((virtualRow) => {
        const row = rowModel.rows[virtualRow.index]!;
        return (
          <RecordTableRowView
            key={row.id}
            ref={(node) => virtualizer.measureElement(node)}
            index={virtualRow.index}
            start={virtualRow.start}
            tableWidth={tableWidth}
            selected={activeCell?.rowId === row.id}
          >
            {row.getAllCells().map((cell) => {
              const tableCell = { rowId: row.id, columnId: cell.column.id };
              const selectable = cell.column.id !== ACTIONS_COLUMN_ID;
              const active =
                selectable &&
                activeCell?.rowId === tableCell.rowId &&
                activeCell.columnId === tableCell.columnId;
              const editing =
                active &&
                editingCell?.rowId === tableCell.rowId &&
                editingCell.columnId === tableCell.columnId;
              return (
                <RecordTableCellView
                  key={cell.id}
                  ref={(element) => {
                    const key = `${tableCell.rowId}:${tableCell.columnId}`;
                    if (element) cellRefs.current.set(key, element);
                    else cellRefs.current.delete(key);
                  }}
                  role={selectable ? "gridcell" : undefined}
                  tabIndex={
                    selectable
                      ? entryCell?.rowId === row.id &&
                        entryCell.columnId === cell.column.id
                        ? 0
                        : -1
                      : undefined
                  }
                  aria-selected={active || undefined}
                  aria-readonly={
                    selectable ? !canEditCell(tableCell) : undefined
                  }
                  data-active-cell={active || undefined}
                  width={cell.column.getSize()}
                  pinned={cell.column.id === ACTIONS_COLUMN_ID}
                  active={active}
                  editing={editing}
                  onFocus={(event) => {
                    if (
                      selectable &&
                      event.target === event.currentTarget &&
                      !active
                    )
                      onSelectCell(tableCell, row.original);
                  }}
                  onKeyDownCapture={(event) => {
                    if (
                      !editing ||
                      !event.currentTarget.contains(event.target as Node)
                    )
                      return;
                    const target = event.target as HTMLElement;
                    const textSubmit =
                      event.key === "Enter" &&
                      (target.tagName === "INPUT" ||
                        (target.tagName === "TEXTAREA" &&
                          (event.ctrlKey || event.metaKey)));
                    if (event.key !== "Escape" && !textSubmit) return;
                    const element = event.currentTarget;
                    pendingFocus.schedule(() => {
                      // An outside click/Tab destination keeps its focus. Keyboard
                      // completion returns only after the editor unmounts or blurs.
                      if (
                        element.isConnected &&
                        document.activeElement === document.body
                      )
                        element.focus();
                    });
                  }}
                  onClick={
                    selectable
                      ? (event) => {
                          if (
                            editing ||
                            !event.currentTarget.contains(event.target as Node)
                          )
                            return;
                          pendingFocus.cancel();
                          onSelectCell(tableCell, row.original);
                          event.currentTarget.focus();
                          if (
                            canEditCell(tableCell) &&
                            editOnClick(tableCell)
                          ) {
                            onEditCell(tableCell);
                            focusCell(tableCell, true);
                          }
                        }
                      : undefined
                  }
                  onDoubleClick={
                    selectable
                      ? () => {
                          if (editing) return;
                          onSelectCell(tableCell, row.original);
                          if (!canEditCell(tableCell)) return;
                          onEditCell(tableCell);
                          focusCell(tableCell, true);
                        }
                      : undefined
                  }
                  onKeyDown={
                    selectable
                      ? (event) => {
                          if (editing) return;
                          if (event.key === "Enter") {
                            if (!canEditCell(tableCell)) return;
                            event.preventDefault();
                            onEditCell(tableCell);
                            focusCell(tableCell, true);
                            return;
                          }
                          if (
                            ![
                              "ArrowUp",
                              "ArrowDown",
                              "ArrowLeft",
                              "ArrowRight",
                            ].includes(event.key)
                          )
                            return;
                          const nextCell = nextRecordTableCell(
                            rowModel.rows.map((candidate) => candidate.id),
                            dataColumns.map((column) => column.id),
                            tableCell,
                            event.key as RecordTableArrowKey,
                          );
                          if (!nextCell) return;
                          const nextRow = rowModel.rows.find(
                            (candidate) => candidate.id === nextCell.rowId,
                          );
                          if (!nextRow) return;
                          event.preventDefault();
                          onSelectCell(nextCell, nextRow.original);
                          focusCell(nextCell);
                        }
                      : undefined
                  }
                >
                  <RecordTableCellContent
                    cell={cell}
                    options={table.options}
                    state={table.state}
                  />
                </RecordTableCellView>
              );
            })}
          </RecordTableRowView>
        );
      })}
    </RecordTableGridView>
  );
}
